// تم تحديث الكود لإضافة عدد الخيول غير المسجلة التي حالتها is_active = 1
// وكذلك إضافة تنبيه بعدم إمكانية إعادة تسجيل الخيل بنفس الغرفة قبل مرور 24 ساعة من تسجيل الخروج

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  Paper,
  Autocomplete,
  Stack,
  LinearProgress,
} from '@mui/material';

import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import { MdOutlineRoomPreferences, MdNoMeetingRoom } from 'react-icons/md';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { useNavigate } from 'react-router-dom';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartActionButtonSx as actionButtonSx,
} from '../../theme/smartStableTheme';
import { todayDateInput } from '../../utils/dateUtils';

const apiUrl = process.env.REACT_APP_API_URL;

const toLocalISODate = (d = new Date()) => {
  return todayDateInput();
};

const getOccupancyPercent = (room) => {
  const count = Number(room?.accommodation_count || 0);
  const capacity = Number(room?.capacity || 0);
  if (!capacity) return count > 0 ? 100 : 0;
  return Math.min(100, Math.round((count / capacity) * 100));
};

const getRoomState = (room) => {
  if (room.status === 'maintenance') return 'maintenance';
  if (Number(room.accommodation_count || 0) <= 0) return 'available';
  if (Number(room.capacity || 0) > 0 && Number(room.accommodation_count || 0) >= Number(room.capacity || 0)) {
    return 'full';
  }
  return 'occupied';
};

const canCheckInToRoom = (room) => {
  if (getRoomState(room) === 'maintenance') return false;
  const capacity = Number(room?.capacity || 0);
  const occupied = Number(room?.accommodation_count || 0);
  return capacity > 0 && occupied < capacity;
};

const getRoomStateStyle = (room) => {
  const state = getRoomState(room);
  if (state === 'maintenance') {
    return {
      accent: '#b95d5d',
      bg: '#fff3f1',
      chipBg: '#f8dedd',
      shadow: 'rgba(185, 93, 93, 0.14)',
    };
  }
  if (state === 'full') {
    return {
      accent: '#c29355',
      bg: '#fff7e8',
      chipBg: '#f5dfbd',
      shadow: 'rgba(194, 147, 85, 0.16)',
    };
  }
  if (state === 'occupied') {
    return {
      accent: '#6f8fa6',
      bg: '#eef7fb',
      chipBg: '#dcecf3',
      shadow: 'rgba(111, 143, 166, 0.14)',
    };
  }
  return {
    accent: '#6f9a7d',
    bg: '#eef8f0',
    chipBg: '#dcefe2',
    shadow: 'rgba(111, 154, 125, 0.14)',
  };
};

const getRoomDisplayName = (room) => {
  const stable = String(room?.stable_name || '').trim();
  const roomName = String(room?.room_name || '').trim();
  if (stable && roomName) return `${stable}-${roomName}`;
  return roomName || stable || '-';
};

const StatCard = ({ title, value, helper, icon, color = COLORS.gold, soft = COLORS.goldSoft }) => (
  <Paper
    elevation={0}
    sx={{
      ...cardSx,
      p: 2,
      minHeight: 104,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <Box>
      <Typography sx={{ color, fontWeight: 900, fontSize: 14 }}>
        {title}
      </Typography>
      <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>
        {value}
      </Typography>
      {helper ? (
        <Typography variant="caption" sx={{ color: COLORS.muted }}>
          {helper}
        </Typography>
      ) : null}
    </Box>
    <Avatar
      sx={{ bgcolor: soft, color, width: 44, height: 44 }}
    >
      {icon}
    </Avatar>
  </Paper>
);

const AvailableRoomsView = () => {
  const { t } = useTranslation();
  const [rooms, setRooms] = useState([]);
  const [selectedHorseId, setSelectedHorseId] = useState(null);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [targetRoomId, setTargetRoomId] = useState('');
  const [transferDate, setTransferDate] = useState(toLocalISODate());

  const [selectHorseDialog, setSelectHorseDialog] = useState({
    open: false,
    room: null,
    mode: null, // 'checkout' | 'transfer'
  });
  const [selectedHorseInDialog, setSelectedHorseInDialog] = useState(null);
  const [horses, setHorses] = useState([]);
  const [activeUnaccommodatedCount, setActiveUnaccommodatedCount] = useState(0);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [horseId, setHorseId] = useState('');
  const [startDate, setStartDate] = useState(toLocalISODate());
  const [dialogHorses, setDialogHorses] = useState([]);
  const [endDate, setEndDate] = useState('');
  const [showOccupiedDialog, setShowOccupiedDialog] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [horseToCheckout, setHorseToCheckout] = useState(null);
  const user_id = localStorage.getItem('userId');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [selectedStables, setSelectedStables] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeId, setTypeId] = useState('');
  const [accommodationTypes, setAccommodationTypes] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [currentRoomId, setCurrentRoomId] = useState(null);
  const navigate = useNavigate();
  // --- Horse search (global + in-dialog)
  const [horseQuery, setHorseQuery] = useState('');
  // const [horseDialogQuery, setHorseDialogQuery] = useState('');
  // Swap dialog states
  const [swapDialog, setSwapDialog] = useState({
    open: false,
    sourceRoomId: null,
    sourceHorseId: null,
  });
  const [swapTargetRoomId, setSwapTargetRoomId] = useState('');
  const [swapTargetHorses, setSwapTargetHorses] = useState([]);
  const [swapTargetHorseId, setSwapTargetHorseId] = useState('');
  const [swapDate, setSwapDate] = useState(toLocalISODate());

  const matchesQuery = useCallback((name, q) => {
    const normalizeStr = (s = '') =>
      s
        .toString()
        .trim()
        .replace(/\u0640/g, '') // حذف التطويل
        .replace(/[\u0617-\u061A\u064B-\u0652]/g, '') // حذف التشكيل
        .toLowerCase();

    return normalizeStr(name).includes(normalizeStr(q));
  }, []);

  // يعيد مصفوفة [{ id: number|null, name: string }]
  const parseRoomHorses = (room) => {
    // جرّب أكثر من حقل وأكثر من فاصل
    /*const rawNames =
      room.horse_names || room.horse_names_en || room.horse_names_ar || '';*/
    const rawIds = room.horse_ids || '';

 const rawNames =
   i18n.language === 'ar'
     ? (room.horse_names_ar || room.horse_names || room.horse_names_en || '')
     : (room.horse_names_en || room.horse_names || room.horse_names_ar || '');


    const SEP = /\s*(?:[,;|،])\s*/;

    const names = String(rawNames)
      .split(SEP) // كان: .split(/[\s,|;،]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    const ids = String(rawIds)
      .split(SEP) // كان: .split(/[\s,|;،]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    // 1) لو الأعداد متساوية
    if (names.length && ids.length && names.length === ids.length) {
      return ids.map((id, i) => ({
        id: Number.isFinite(Number(id)) ? Number(id) : null,
        name: names[i],
      }));
    }

    // 2) عند توفر المعرفات فقط
    if (ids.length && !names.length) {
      // لو فيه اسم واحد عام في الغرفة استخدمه، وإلا اكتب ID
      const fallbackName = room.horse_name || room.name || null;
      return ids.map((id) => ({
        id: Number.isFinite(Number(id)) ? Number(id) : null,
        name: fallbackName ? fallbackName : `#${id}`,
      }));
    }

    // 3) عند توفر الأسماء فقط
    if (names.length && !ids.length) {
      // لو فيه horse_id منفرد استخدمه لأول عنصر
      if (room.horse_id) {
        return [
          { id: Number(room.horse_id), name: names[0] },
          ...names.slice(1).map((n) => ({ id: null, name: n })),
        ];
      }
      // أعرض الأسماء بدون معرفات (تبقى قابلة للعرض فقط)
      return names.map((n) => ({ id: null, name: n }));
    }

    // 4) أطوال غير متساوية: قرن المتاح وأكمل بالباقي
    const maxLen = Math.max(names.length, ids.length);
    const out = [];
    for (let i = 0; i < maxLen; i++) {
      const idStr = ids[i];
      const id = Number.isFinite(Number(idStr))
        ? Number(idStr)
        : room.horse_id
        ? Number(room.horse_id)
        : null;
      const name =
        names[i] ||
        names[0] ||
        room.horse_name ||
        room.name ||
        (id != null ? `#${id}` : 'Horse');
      out.push({ id, name });
    }
    return out;
  };

  const uniqueStables = [...new Set(rooms.map((r) => r.stable_name))];
  const handleStableFilterChange = (stableName) => {
    setSelectedStables((prevSelected) =>
      prevSelected.includes(stableName)
        ? prevSelected.filter((s) => s !== stableName)
        : [...prevSelected, stableName]
    );
  };

  useEffect(() => {
    fetchRooms();
    fetchUnaccommodatedHorses();
    fetchAccommodationTypes();
  }, []);

  const fetchAccommodationTypes = async () => {
    try {
      const res = await axios.get(`${apiUrl}/accommodations/types`);
      setAccommodationTypes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching types:', err);
    }
  };

  const fetchRooms = async () => {
    try {
      const res = await axios.get(`${apiUrl}/rooms/rooms-with-stable-count`);
      setRooms(res.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };
  const handleTransferClick = async (horseId, roomId) => {
    setSelectedHorseId(horseId);
    setCurrentRoomId(roomId); // حفظ الغرفة الحالية
    setTargetRoomId(''); //setTargetRoomId(roomId.toString());
    setTypeId('');
    setTransferDate(toLocalISODate());
    try {
      const res = await axios.get(
        `${apiUrl}/accommodations/current/${horseId}`
      );
      if (res.data?.type_id) setTypeId(res.data.type_id.toString());
    } catch (err) {
      console.error('Error fetching current type_id:', err);
    }

    setTransferDialogOpen(true);
  };

  const handleTransferSubmit = async () => {
    if (!user_id) {
      console.error('user_id غير متوفر في localStorage');
      setSnackbar({
        open: true,
        message: 'خطأ: الرجاء تسجيل الدخول.',
        severity: 'error',
      });
      return;
    }
    if (!typeId || Number(typeId) <= 0) {
      setSnackbar({
        open: true,
        message: t('accommodationList.typeRequired'),
        severity: 'error',
      });
      return;
    }
    if (!targetRoomId || Number(targetRoomId) <= 0) {
      setSnackbar({
        open: true,
        message: t('accommodationList.targetRoomRequired'),
        severity: 'error',
      });
      return;
    }
    try {
      await axios.post(`${apiUrl}/accommodations/transfer`, {
        horse_id: selectedHorseId,
        new_room_id: Number(targetRoomId),
        transfer_date: transferDate,
        user_id: user_id,
        type_id: Number(typeId),
      });

      setSnackbar({
        open: true,
        message: t('accommodationList.transferSuccess'),
        severity: 'success',
      });

      setTransferDialogOpen(false);
      setTargetRoomId('');
      fetchRooms();
    } catch (err) {
      console.error('Transfer error:', err);

      const apiMsg =
        err?.response?.data?.message || err?.response?.data?.error || '';
      const is24h = /24\s*ساعة|24h|24\s*hours|REENTRY_24H/i.test(apiMsg);
      const errorMessage = is24h
        ? t('accommodationList.reentryDelayNotice')
        : apiMsg || t('accommodationList.transferError');
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };
  const fetchUnaccommodatedHorses = async () => {
    try {
      const res = await axios.get(`${apiUrl}/unaccommodated`);
      setHorses(res.data);
      const activeCount = res.data.filter((h) => h.is_active === 1).length;
      setActiveUnaccommodatedCount(activeCount);
    } catch (error) {
      console.error('Error fetching unaccommodated horses:', error);
    }
  };

  const openDialogWithRoom = (roomId) => {
    setSelectedRoomId(roomId);
    setTypeId('');
    fetchUnaccommodatedHorses();
    setDialogOpen(true);
  };

  const handleSaveAccommodation = async () => {
    if (!selectedRoomId || Number(selectedRoomId) <= 0) {
      setSnackbar({
        open: true,
        message: t('accommodationList.roomRequired'),
        severity: 'error',
      });
      return;
    }
    if (!horseId || Number(horseId) <= 0) {
      setSnackbar({
        open: true,
        message: t('accommodationList.horseRequired'),
        severity: 'error',
      });
      return;
    }
    if (endDate && new Date(endDate) < new Date(startDate)) {
      setSnackbar({
        open: true,
        message: t('accommodationList.invalidEndDate'),
        severity: 'error',
      });
      return;
    }
    if (!typeId || Number(typeId) <= 0) {
      setSnackbar({
        open: true,
        message: t('accommodationList.typeRequired'),
        severity: 'error',
      });
      return;
    }
    if (!user_id) {
      setSnackbar({
        open: true,
        message: t('accommodationList.loginRequired'),
        severity: 'error',
      });
      return;
    }

    try {
      const data = {
        horse_id: Number(horseId),
        room_id: Number(selectedRoomId),
        start_date: startDate,
        end_date: endDate || null,
        user_id: user_id,
        type_id: Number(typeId),
      };
      const response = await axios.post(`${apiUrl}/accommodations`, data);
      setSnackbar({
        open: true,
        message: response.data?.reopened
          ? t('accommodationList.reopenSuccess')
          : response.data.message || t('accommodationList.addSuccess'),
        severity: 'success',
      });
      setDialogOpen(false);
      setHorseId('');
      setEndDate('');
      setTypeId('');
      setStartDate(toLocalISODate());
      fetchRooms();
    } catch (error) {
      const apiMsg =
        error?.response?.data?.message || error?.response?.data?.error || '';
      const is24h = /24\s*ساعة|24h|24\s*hours|REENTRY_24H/i.test(apiMsg);
      const errorMessage = is24h
        ? t('accommodationList.reentryDelayNotice')
        : apiMsg || t('accommodationList.addError');
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const toggleRoomStatus = async (room) => {
    try {
      if (room.status !== 'maintenance' && room.accommodation_count > 0) {
        setShowOccupiedDialog(true);
        return;
      }
      const endpoint =
        room.status === 'maintenance' ? 'available' : 'maintenance';
      await axios.put(`${apiUrl}/rooms/${room.room_id}/${endpoint}`);
      const messageKey =
        endpoint === 'available'
          ? 'accommodationList.setAvailableSuccess'
          : 'accommodationList.setMaintenanceSuccess';
      setSnackbar({ open: true, message: t(messageKey), severity: 'success' });
      fetchRooms();
    } catch (error) {
      console.error('Error toggling room status:', error);
    }
  };

  const confirmCheckout = async (horseId, roomId) => {
    try {
      const res = await axios.get(
        `${apiUrl}/accommodations/current/${horseId}`
      );
      // بعض البيئات ترجع id، وأخرى accommodation_id — ندعم الحالتين
      const accId = res.data?.id || res.data?.accommodation_id || null;
      setHorseToCheckout({ horseId, accId, roomId });
    } catch {
      // ما فيه سجل نشط؟ نكمل احتياطيًا بـ horseId + roomId
      setHorseToCheckout({ horseId, accId: null, roomId });
    } finally {
      setCheckoutDialogOpen(true);
    }
  };

  const handleCheckoutConfirmed = async () => {
    const { accId, horseId: hId, roomId } = horseToCheckout || {};

    if ((!accId && !hId) || !user_id) {
      console.error('❌ Missing Data ->', { accId, hId, roomId, user_id });
      return;
    }

    try {
      await axios.post(`${apiUrl}/accommodations/checkout`, {
        accommodation_id: accId || undefined,
        horse_id: accId ? undefined : hId, // لو ما عندنا accId
        room_id: accId ? undefined : roomId, // نمرر الغرفة لتثبيت السجل الصحيح
        end_date: toLocalISODate(),
        user_id: localStorage.getItem('userId'),
      });

      setSnackbar({
        open: true,
        message: t('accommodationList.checkoutSuccess'),
        severity: 'success',
      });

      setCheckoutDialogOpen(false);
      setHorseToCheckout(null);
      fetchRooms();
    } catch (error) {
      console.error(
        '❌ Error checking out horse:',
        error.response?.data || error
      );
    }
  };

  const getRoomColor = (room) => {
    return getRoomStateStyle(room).bg;
  };

  const roomSummary = useMemo(() => {
    const totalCapacity = rooms.reduce((sum, room) => sum + Number(room.capacity || 0), 0);
    const occupiedSlots = rooms.reduce((sum, room) => sum + Number(room.accommodation_count || 0), 0);
    const availableRooms = rooms.filter((room) => getRoomState(room) === 'available').length;
    const occupiedRooms = rooms.filter((room) => ['occupied', 'full'].includes(getRoomState(room))).length;
    const maintenanceRooms = rooms.filter((room) => getRoomState(room) === 'maintenance').length;
    const fullRooms = rooms.filter((room) => getRoomState(room) === 'full').length;
    return {
      totalCapacity,
      occupiedSlots,
      availableSlots: Math.max(totalCapacity - occupiedSlots, 0),
      occupancyRate: totalCapacity ? Math.round((occupiedSlots / totalCapacity) * 100) : 0,
      availableRooms,
      occupiedRooms,
      maintenanceRooms,
      fullRooms,
    };
  }, [rooms]);

  const visibleRooms = useMemo(
    () =>
      rooms.filter((room) => {
        const stableOk = selectedStables.length === 0 || selectedStables.includes(room.stable_name);
        const statusOk = statusFilter === 'all' || getRoomState(room) === statusFilter;
        const queryOk = horseQuery
          ? (() => {
              const en = String(room.horse_names || room.horse_names_en || '');
              const ar = String(room.horse_names_ar || '');
              if (!en && !ar && room.accommodation_count === 0) return false;
              return matchesQuery(en, horseQuery) || matchesQuery(ar, horseQuery);
            })()
          : true;
        return stableOk && statusOk && queryOk;
      }),
    [horseQuery, matchesQuery, rooms, selectedStables, statusFilter]
  );

  const groupedByStable = visibleRooms.reduce((acc, room) => {
    if (!acc[room.stable_name]) acc[room.stable_name] = [];
    acc[room.stable_name].push(room);
    return acc;
  }, {});

  const fetchRoomActiveHorses = async (roomId) => {
    try {
      const res = await axios.get(
        `${apiUrl}/accommodations/room/${roomId}/horses`
      );
      setDialogHorses(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Error loading room horses:', e);
      setDialogHorses([]);
    }
  };
  // Open swap for a room: if multiple horses -> ask user to pick source horse first
  const openSwapForRoom = (room) => {
    const horses = parseRoomHorses(room);
    if (!horses || horses.length === 0) return;

    if (horses.length > 1 || !horses[0]?.id) {
      // Reuse existing selectHorseDialog to choose the source horse
      setSelectHorseDialog({ open: true, room, mode: 'swap' });
      setSelectedHorseInDialog(null);
      fetchRoomActiveHorses(room.room_id);
      return;
    }

    // Single horse with ID
    setSwapDialog({
      open: true,
      sourceRoomId: room.room_id,
      sourceHorseId: horses[0].id,
    });
  };

  // Load active horses in target room to pick whom to swap with
  const loadSwapTargetHorses = async (roomId) => {
    try {
      const res = await axios.get(
        `${apiUrl}/accommodations/room/${roomId}/horses`
      );
      setSwapTargetHorses(Array.isArray(res.data) ? res.data : []);
    } catch {
      setSwapTargetHorses([]);
    }
  };

  // Submit swap action
  const handleSwapSubmit = async () => {
    if (!user_id) {
      setSnackbar({
        open: true,
        message: t('accommodationList.loginRequired'),
        severity: 'error',
      });
      return;
    }
    if (!swapDialog.sourceRoomId || !swapDialog.sourceHorseId) {
      setSnackbar({
        open: true,
        message: t('accommodationList.horseRequired'),
        severity: 'error',
      });
      return;
    }
    if (!swapTargetRoomId || !swapTargetHorseId) {
      setSnackbar({
        open: true,
        message: t('accommodationList.targetRoomRequired'),
        severity: 'error',
      });
      return;
    }

    const src = rooms.find(
      (r) => r.room_id === Number(swapDialog.sourceRoomId)
    );
    const tgt = rooms.find((r) => r.room_id === Number(swapTargetRoomId));

    const srcHorseName =
      (src &&
        parseRoomHorses(src).find(
          (h) => String(h.id) === String(swapDialog.sourceHorseId)
        )?.name) ||
      `#${swapDialog.sourceHorseId}`;
    const tgtHorseName =
      swapTargetHorses.find(
        (h) => String(h.horse_id) === String(swapTargetHorseId)
      )?.display_name || `#${swapTargetHorseId}`;

    const lines = [
      t('accommodationList.confirmSwapMsg'),
      `${t('accommodationList.source')}: ${src?.stable_name} - ${
        src?.room_name
      } (${srcHorseName})`,
      `${t('accommodationList.target')}: ${tgt?.stable_name} - ${
        tgt?.room_name
      } (${tgtHorseName})`,
    ];
    const ok = window.confirm(lines.join('\n'));

    if (!ok) return;

    try {
      await axios.post(`${apiUrl}/accommodations/swap`, {
        source_horse_id: Number(swapDialog.sourceHorseId),
        source_room_id: Number(swapDialog.sourceRoomId),
        target_horse_id: Number(swapTargetHorseId),
        target_room_id: Number(swapTargetRoomId),
        swap_date: swapDate,
        user_id,
      });

      setSnackbar({
        open: true,
        message: t('accommodationList.swapSuccess'),
        severity: 'success',
      });
      setSwapDialog({ open: false, sourceRoomId: null, sourceHorseId: null });
      setSwapTargetRoomId('');
      setSwapTargetHorseId('');
      setSwapTargetHorses([]);
      setSwapDate(toLocalISODate());
      fetchRooms();
    } catch (err) {
      const apiMsg =
        err?.response?.data?.message || err?.response?.data?.error || '';
      const is24h = /REENTRY_24H|24\s*ساعة|24h|24\s*hours/i.test(apiMsg);
      setSnackbar({
        open: true,
        message: is24h
          ? t('accommodationList.reentryDelayNotice')
          : apiMsg || t('accommodationList.swapError'),
        severity: 'error',
      });
    }
  };

  return (
    <Box
      className="smart-page-container"
      sx={{
        bgcolor: COLORS.page,
        minHeight: '100%',
        width: '100%',
        maxWidth: 'var(--app-page-max)',
        mx: 'auto',
        overflowX: 'hidden',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          ...cardSx,
          p: { xs: 2, md: 2.5 },
          mb: 2.5,
          display: 'flex',
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900, letterSpacing: 0 }}>
            {t('accommodationList.availableRoomsView') || 'Available rooms'}
          </Typography>
          <Typography sx={{ color: COLORS.gold, mt: 0.4 }}>
            {i18n.language === 'ar'
              ? 'إدارة إشغال الغرف، التسكين، الخروج، النقل والصيانة'
              : 'Manage occupancy, check-in, checkout, transfers, and maintenance'}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ minWidth: { md: 520 } }}>
          <TextField
            value={horseQuery}
            onChange={(e) => setHorseQuery(e.target.value)}
            size="small"
            fullWidth
            label={t('accommodationList.searchHorse')}
            placeholder={t('accommodationList.searchHorsePlaceholder')}
          />
          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            label={i18n.language === 'ar' ? 'الحالة' : 'Status'}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="all">{i18n.language === 'ar' ? 'كل الغرف' : 'All rooms'}</MenuItem>
            <MenuItem value="available">{t('accommodationList.available')}</MenuItem>
            <MenuItem value="occupied">{t('accommodationList.occupied')}</MenuItem>
            <MenuItem value="full">{i18n.language === 'ar' ? 'ممتلئة' : 'Full'}</MenuItem>
            <MenuItem value="maintenance">{t('accommodationList.maintenance')}</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <StatCard
          title={t('accommodationList.totalRooms')}
          value={rooms.length}
          helper={`${roomSummary.occupiedSlots}/${roomSummary.totalCapacity || 0} ${i18n.language === 'ar' ? 'سعة مستخدمة' : 'slots used'}`}
          icon={<MeetingRoomIcon />}
          color={COLORS.gold}
          soft={COLORS.goldSoft}
        />
        <StatCard
          title={i18n.language === 'ar' ? 'نسبة الإشغال' : 'Occupancy rate'}
          value={`${roomSummary.occupancyRate}%`}
          helper={`${roomSummary.availableSlots} ${i18n.language === 'ar' ? 'أماكن متاحة' : 'available slots'}`}
          icon={<MdNoMeetingRoom />}
          color={COLORS.green}
          soft={COLORS.greenSoft}
        />
        <StatCard
          title={t('accommodationList.availableRooms')}
          value={roomSummary.availableRooms}
          helper={`${roomSummary.fullRooms} ${i18n.language === 'ar' ? 'غرف ممتلئة' : 'full rooms'}`}
          icon={<MeetingRoomIcon />}
          color={COLORS.blue}
          soft={COLORS.blueSoft}
        />
        <StatCard
          title={t('accommodationList.maintenanceRooms')}
          value={roomSummary.maintenanceRooms}
          helper={`${roomSummary.occupiedRooms} ${i18n.language === 'ar' ? 'غرف مشغولة' : 'occupied rooms'}`}
          icon={<MdOutlineRoomPreferences />}
          color={COLORS.rose}
          soft={COLORS.roseSoft}
        />
        <StatCard
          title={t('accommodationList.unregisteredActiveHorses')}
          value={activeUnaccommodatedCount}
          helper={i18n.language === 'ar' ? 'جاهزة للتسكين' : 'ready for check-in'}
          icon={<LoginIcon />}
          color={COLORS.warning}
          soft={COLORS.warningSoft}
        />
      </Box>

      <Paper elevation={0} sx={{ ...cardSx, p: 1.5, mb: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
          <Typography sx={{ color: COLORS.muted, fontWeight: 900, px: 0.5 }}>
            {i18n.language === 'ar' ? 'الإسطبلات' : 'Stables'}
          </Typography>
          {uniqueStables.map((stable) => (
            <Chip
              key={stable}
              label={stable}
              clickable
              onClick={() => handleStableFilterChange(stable)}
              sx={{
                bgcolor: selectedStables.includes(stable) ? COLORS.gold : COLORS.cardSoft,
                color: selectedStables.includes(stable) ? '#fff' : COLORS.text,
                border: `1px solid ${selectedStables.includes(stable) ? COLORS.gold : COLORS.line}`,
                fontWeight: 800,
              }}
            />
          ))}
          {selectedStables.length > 0 ? (
            <Button size="small" onClick={() => setSelectedStables([])} sx={{ color: COLORS.gold, fontWeight: 900 }}>
              {i18n.language === 'ar' ? 'إظهار الكل' : 'Show all'}
            </Button>
          ) : null}
        </Stack>
      </Paper>

      {Object.entries(groupedByStable)
        .map(([stableName, stableRooms]) => {
          if (stableRooms.length === 0) return null;

          const sortedRooms = [...stableRooms].sort((a, b) =>
            a.room_name.localeCompare(b.room_name, 'ar', { numeric: true })
          );
          const stableUsed = stableRooms.reduce((sum, room) => sum + Number(room.accommodation_count || 0), 0);
          const stableCapacity = stableRooms.reduce((sum, room) => sum + Number(room.capacity || 0), 0);
          const stableOccupancyPercent = stableCapacity
            ? Math.min(100, Math.round((stableUsed / stableCapacity) * 100))
            : 0;

          return (
            <Box key={stableName} sx={{ mb: 3 }}>
              <Paper
                elevation={0}
                sx={{
                  ...cardSx,
                  p: 2,
                  mb: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', md: 'center' },
                  gap: 1,
                  flexDirection: { xs: 'column', md: 'row' },
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
                    {`${t('accommodationList.stable')}: ${stableName}`}
                  </Typography>
                  <Typography variant="body2" sx={{ color: COLORS.muted }}>
                    {stableRooms.length} {i18n.language === 'ar' ? 'غرف' : 'rooms'} · {stableUsed}/{stableCapacity || 0}{' '}
                    {i18n.language === 'ar' ? 'إشغال' : 'occupied slots'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ width: { xs: '100%', md: 330 } }}>
                  <LinearProgress
                    variant="determinate"
                    value={stableOccupancyPercent}
                    sx={{
                      flex: 1,
                      height: 8,
                      borderRadius: 999,
                      bgcolor: COLORS.cardSoft,
                      '& .MuiLinearProgress-bar': { bgcolor: COLORS.gold, borderRadius: 999 },
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      minWidth: 44,
                      color: COLORS.gold,
                      fontWeight: 900,
                      textAlign: 'end',
                    }}
                  >
                    {stableOccupancyPercent}%
                  </Typography>
                </Stack>
              </Paper>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(3, minmax(0, 1fr))',
                    lg: 'repeat(4, minmax(0, 1fr))',
                  },
                  gap: 1.5,
                  width: '100%',
                }}
              >
                {sortedRooms.map((room) => {
                  const roomState = getRoomState(room);
                  const roomStyle = getRoomStateStyle(room);
                  const roomHorses = parseRoomHorses(room);
                  const canCheckIn = canCheckInToRoom(room);
                  const statusLabel =
                    roomState === 'maintenance'
                      ? t('accommodationList.maintenance')
                      : roomState === 'available'
                      ? t('accommodationList.available')
                      : roomState === 'full'
                      ? i18n.language === 'ar'
                        ? 'ممتلئة'
                        : 'Full'
                      : t('accommodationList.occupied');
                  return (
                  <Box key={room.room_id} sx={{ minWidth: 0 }}>
                    <Card
                      sx={{
                        backgroundColor: getRoomColor(room),
                        minHeight: 178,
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${roomStyle.chipBg}`,
                        borderInlineStart: `6px solid ${roomStyle.accent}`,
                        boxShadow: `0 10px 24px ${roomStyle.shadow}`,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        color: COLORS.text,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          inset: '0 0 auto 0',
                          height: 4,
                          bgcolor: roomStyle.accent,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 0, pb: '8px !important' }}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" noWrap sx={{ color: COLORS.text, fontWeight: 900 }}>
                              {getRoomDisplayName(room)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: COLORS.muted }}>
                              {room.stable_name}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={statusLabel}
                            sx={{
                              bgcolor: roomStyle.chipBg,
                              color: roomStyle.accent,
                              border: `1px solid ${roomStyle.accent}`,
                              fontWeight: 900,
                            }}
                          />
                        </Stack>

                        <Box sx={{ mt: 1.2 }}>
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 800 }}>
                              {i18n.language === 'ar' ? 'الإشغال' : 'Occupancy'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: COLORS.text, fontWeight: 900 }}>
                              {Number(room.accommodation_count || 0)}/{Number(room.capacity || 0)}
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={getOccupancyPercent(room)}
                            sx={{
                              height: 7,
                              borderRadius: 999,
                              bgcolor: '#fffdf8',
                              '& .MuiLinearProgress-bar': { bgcolor: roomStyle.accent, borderRadius: 999 },
                            }}
                          />
                        </Box>

                        {roomHorses.length > 0 ? (
                          <Box
                            sx={{
                              mt: 1.2,
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 0.5,
                            }}
                          >
                            {roomHorses.map((h, horseIndex) => (
                              <Chip
                                key={`${room.room_id}-${h.id ?? 'noid'}-${horseIndex}`}
                                label={
                                  `${h.name}` +
                                  (room.type_name_ar || room.type_name_en
                                    ? ` (${
                                        i18n.language === 'ar'
                                          ? room.type_name_ar
                                          : room.type_name_en
                                      })`
                                    : '')
                                }
                                size="small"
                                sx={{
                                  bgcolor: COLORS.card,
                                  color: COLORS.text,
                                  border: `1px solid ${COLORS.line}`,
                                  maxWidth: '100%',
                                  cursor: h.id ? 'pointer' : 'default',
                                  opacity: h.id ? 1 : 0.85,
                                }}
                                onClick={
                                  h.id
                                    ? () => navigate(`/horse-details/${h.id}`)
                                    : undefined
                                }
                                clickable={!!h.id}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: COLORS.muted, mt: 1.2 }}>
                            {i18n.language === 'ar' ? 'لا توجد خيول في هذه الغرفة' : 'No horses in this room'}
                          </Typography>
                        )}
                      </CardContent>

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          gap: 0.75,
                          pt: 1,
                          borderTop: `1px solid ${COLORS.line}`,
                        }}
                      >
                        <Tooltip title={t('accommodationList.setMaintenance')}>
                          <IconButton
                            size="small"
                            sx={actionButtonSx(COLORS.warning, COLORS.warningSoft, '#f2dfc3')}
                            onClick={() => toggleRoomStatus(room)}
                          >
                            <SettingsSuggestIcon />
                          </IconButton>
                        </Tooltip>
                        {room.status === 'available' &&
                          room.accommodation_count > 0 && (
                            <Tooltip title={t('accommodationList.swap')}>
                              <IconButton
                                onClick={() => openSwapForRoom(room)}
                                size="small"
                                sx={actionButtonSx(COLORS.blue, COLORS.blueSoft, '#dbeaf1')}
                              >
                                <CompareArrowsIcon />
                              </IconButton>
                            </Tooltip>
                          )}

                        {canCheckIn && (
                            <Tooltip title={t('accommodationList.checkin')}>
                              <IconButton
                                size="small"
                                sx={actionButtonSx(COLORS.green, COLORS.greenSoft, '#dcefe2')}
                                onClick={() => openDialogWithRoom(room.room_id)}
                              >
                                <LoginIcon />
                              </IconButton>
                            </Tooltip>
                        )}

                        {(room.horse_id ||
                          parseRoomHorses(room).length > 0) && (
                          <Tooltip title={t('accommodationList.transferHorse')}>
                            <IconButton
                              size="small"
                              sx={actionButtonSx(COLORS.gold, COLORS.goldSoft, '#e7d7b4')}
                              onClick={() => {
                                const horses = parseRoomHorses(room);

                                if (
                                  (horses?.length || 0) > 1 ||
                                  !horses?.[0]?.id
                                ) {
                                  setSelectHorseDialog({
                                    open: true,
                                    room,
                                    mode: 'transfer',
                                  });
                                  setSelectedHorseInDialog(null);
                                  fetchRoomActiveHorses(room.room_id);
                                } else {
                                  handleTransferClick(
                                    horses[0].id,
                                    room.room_id
                                  );
                                }
                              }}
                            >
                              <AltRouteIcon />
                            </IconButton>
                          </Tooltip>
                        )}

                        {!(room.status === 'available' && room.accommodation_count === 0) && (
                          <Tooltip title={t('accommodationList.checkout')}>
                            <span>
                              <IconButton
                                size="small"
                                sx={actionButtonSx(COLORS.rose, COLORS.roseSoft, '#efd9d6')}
                                onClick={() => {
                                  const horses = parseRoomHorses(room);

                                  // لا يوجد خيول في الغرفة
                                  if (!horses || horses.length === 0) return;

                                  // لو أكثر من حصان أو حصان واحد لكن بدون ID → افتح حوار الاختيار (يعتمد على API موثوقة)
                                  if (horses.length > 1 || !horses[0]?.id) {
                                    setSelectHorseDialog({
                                      open: true,
                                      room,
                                      mode: 'checkout',
                                    });
                                    setSelectedHorseInDialog(null);
                                    fetchRoomActiveHorses(room.room_id);
                                    return;
                                  }

                                  // حصان واحد ومعه ID واضح → أكمل مباشرة
                                  confirmCheckout(horses[0].id, room.room_id);
                                }}
                                disabled={
                                  !room.horse_id &&
                                  parseRoomHorses(room).length === 0
                                }
                              >
                                <LogoutIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </Card>
                  </Box>
                );
                })}
              </Box>
            </Box>
          );
        })}

      <Dialog
        open={transferDialogOpen}
        onClose={() => setTransferDialogOpen(false)}
      >
        <DialogTitle>{t('accommodationList.transferHorse')}</DialogTitle>

        <DialogContent sx={{ px: 3, pt: 2 }}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>{t('accommodationList.targetRoom')}</InputLabel>
            <Select
              value={targetRoomId}
              onChange={(e) => setTargetRoomId(e.target.value)}
              label={t('accommodationList.targetRoom')}
            >
              <MenuItem value="">
                {t('accommodationList.selectTargetRoom')}
              </MenuItem>
              {rooms
                .filter(
                  (r) => r.status === 'available' && r.room_id !== currentRoomId
                )
                .map((room) => (
                  <MenuItem key={room.room_id} value={String(room.room_id)}>
                    {room.stable_name} - {room.room_name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>{t('accommodationList.accommodationType')}</InputLabel>
            <Select
              value={typeId}
              onChange={(e) => setTypeId(e.target.value)}
              label={t('accommodationList.accommodationType')}
            >
              {accommodationTypes.map((type) => (
                <MenuItem key={type.type_id} value={String(type.type_id)}>
                  {i18n.language === 'ar'
                    ? type.type_name_ar
                    : type.type_name_en}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label={t('accommodationList.transferDate')}
            type="date"
            value={transferDate}
            onChange={(e) => setTransferDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTransferDialogOpen(false)}>
            {t('accommodationList.cancel')}
          </Button>
          <Button onClick={handleTransferSubmit} variant="contained">
            {t('accommodationList.transfer')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{t('accommodationList.addAccommodation')}</DialogTitle>
        <DialogContent>
          {/* اختـيار الخيل مع بحث — لا تضعه داخل FormControl فيه InputLabel */}
          <Box sx={{ mb: 2 }}>
            <Autocomplete
              /*options={horses}
              // لو تحب تستخدم list المفلترة مسبقًا استبدل horses بـ filteredUnaccommodated
              value={horses.find((h) => h.horse_id === horseId) || null}
              onChange={(_, newVal) => setHorseId(newVal?.horse_id || '')}
              getOptionLabel={(option) => option?.name || ''}*/

              options={horses}
              value={horses.find((h) => h.horse_id === horseId) || null}
              onChange={(_, newVal) => setHorseId(newVal?.horse_id || '')}
              getOptionLabel={(option) =>
                i18n.language === 'ar'
                  ? option?.name_arabic || option?.name || ''
                  : option?.name || option?.name_arabic || ''
              }
              // فلترة مخصصة تدعم العربية (بدون تشكيل/تمطيط)
              /*filterOptions={(options, state) =>
                options.filter((o) =>
                  matchesQuery(o?.name || '', state.inputValue || '')
                )
              }*/
              filterOptions={(options, state) => {
                const q = state.inputValue || '';
                return options.filter(
                  (o) =>
                    matchesQuery(o?.name || '', q) ||
                    matchesQuery(o?.name_arabic || '', q)
                );
              }}
              noOptionsText={t('accommodationList.noHorses')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('accommodationList.horseName')}
                  placeholder={t('accommodationList.searchHorsePlaceholder')}
                  size="small"
                  fullWidth
                />
              )}
              ListboxProps={{ style: { maxHeight: 260 } }}
              // اختياري: حدث كل تغيير نصي لو تبغى تحفظه في horseDialogQuery
              //onInputChange={(_, v) => setHorseDialogQuery(v)}
            />
          </Box>

          {/* نوع الإقامة في FormControl مستقل */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="accom-type-label">
              {t('accommodationList.accommodationType')}
            </InputLabel>
            <Select
              labelId="accom-type-label"
              value={typeId}
              onChange={(e) => setTypeId(e.target.value)}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <span style={{ opacity: 0.6 }}>
                      {t('accommodationList.selectType') || 'Select type'}
                    </span>
                  );
                }
                const found = accommodationTypes.find(
                  (x) => String(x.type_id) === String(selected)
                );
                return i18n.language === 'ar'
                  ? found?.type_name_ar
                  : found?.type_name_en;
              }}
              MenuProps={{ PaperProps: { style: { maxHeight: 260 } } }}
            >
              {accommodationTypes.map((type) => (
                <MenuItem key={type.type_id} value={String(type.type_id)}>
                  {i18n.language === 'ar'
                    ? type.type_name_ar
                    : type.type_name_en}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* التواريخ كما هي */}
          <TextField
            fullWidth
            label={t('accommodationList.startDate')}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('accommodationList.endDateOptional')}
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {t('accommodationList.cancel')}
          </Button>
          <Button onClick={handleSaveAccommodation} variant="contained">
            {t('accommodationList.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showOccupiedDialog}
        onClose={() => setShowOccupiedDialog(false)}
      >
        <DialogTitle>
          {t('accommodationList.occupiedRoomMaintenanceWarningTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('accommodationList.occupiedRoomMaintenanceWarningMessage')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOccupiedDialog(false)}>
            {t('accommodationList.ok')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={checkoutDialogOpen}
        onClose={() => setCheckoutDialogOpen(false)}
      >
        <DialogTitle>{t('accommodationList.confirmCheckoutTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('accommodationList.reentryDelayNotice')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckoutDialogOpen(false)}>
            {t('accommodationList.cancel')}
          </Button>
          <Button onClick={handleCheckoutConfirmed} variant="contained">
            {t('accommodationList.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* ⬇⬇⬇ أضِف هذا الحوار بعد حوار تأكيد الخروج مباشرة ⬇⬇⬇ */}
      <Dialog
        open={selectHorseDialog.open}
        onClose={() =>
          setSelectHorseDialog({ open: false, room: null, mode: null })
        }
      >
        <DialogTitle>
          {selectHorseDialog.mode === 'checkout'
            ? t('accommodationList.selectHorseForCheckout')
            : selectHorseDialog.mode === 'transfer'
            ? t('accommodationList.selectHorseForTransfer')
            : t('accommodationList.selectHorseForSwap')}
        </DialogTitle>

        <DialogContent>
          <FormControl fullWidth>
            <InputLabel>{t('accommodationList.horse')}</InputLabel>
            <Select
              label={t('accommodationList.horse')}
              value={selectedHorseInDialog || ''}
              onChange={(e) => setSelectedHorseInDialog(e.target.value)}
              MenuProps={{ PaperProps: { style: { maxHeight: 260 } } }}
            >
              <MenuItem value="">{t('accommodationList.selectHorse')}</MenuItem>
              {dialogHorses.map((h) => (
                <MenuItem key={h.horse_id} value={h.horse_id}>
                  {h.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setSelectHorseDialog({ open: false, room: null, mode: null })
            }
          >
            {t('accommodationList.cancel')}
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              if (!selectedHorseInDialog) return;
              const roomId = selectHorseDialog.room?.room_id;

              if (selectHorseDialog.mode === 'checkout') {
                confirmCheckout(selectedHorseInDialog, roomId);
              } else if (selectHorseDialog.mode === 'transfer') {
                handleTransferClick(selectedHorseInDialog, roomId);
              } else if (selectHorseDialog.mode === 'swap') {
                // Open swap dialog with the chosen source horse
                setSwapDialog({
                  open: true,
                  sourceRoomId: roomId,
                  sourceHorseId: selectedHorseInDialog,
                });
              }

              setSelectHorseDialog({ open: false, room: null, mode: null });
              setSelectedHorseInDialog(null);
            }}
          >
            {t('accommodationList.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* ⬆⬆⬆ نهاية حوار اختيار الخيل ⬆⬆⬆ */}
      <Dialog
        open={swapDialog.open}
        onClose={() => {
          setSwapDialog({
            open: false,
            sourceRoomId: null,
            sourceHorseId: null,
          });
          setSwapTargetRoomId('');
          setSwapTargetHorseId('');
          setSwapTargetHorses([]);
        }}
      >
        <DialogTitle>{t('accommodationList.swap')}</DialogTitle>
        <DialogContent sx={{ minWidth: 360 }}>
          <Typography sx={{ mb: 2 }}>
            {t('accommodationList.swapHint')}
          </Typography>

          {/* Target room */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('accommodationList.targetRoom')}</InputLabel>
            <Select
              label={t('accommodationList.targetRoom')}
              value={swapTargetRoomId}
              onChange={(e) => {
                const val = e.target.value;
                setSwapTargetRoomId(val);
                if (val) loadSwapTargetHorses(val);
                setSwapTargetHorseId('');
              }}
              MenuProps={{ PaperProps: { style: { maxHeight: 260 } } }}
            >
              <MenuItem value="">
                {t('accommodationList.selectTargetRoom')}
              </MenuItem>
              {rooms
                .filter(
                  (r) =>
                    r.status === 'available' &&
                    r.room_id !== swapDialog.sourceRoomId &&
                    r.accommodation_count > 0 // swap only if target has a horse
                )
                .map((r) => (
                  <MenuItem key={r.room_id} value={String(r.room_id)}>
                    {r.stable_name} - {r.room_name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Target room horse */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('accommodationList.horse')}</InputLabel>
            <Select
              label={t('accommodationList.horse')}
              value={swapTargetHorseId}
              onChange={(e) => setSwapTargetHorseId(e.target.value)}
              MenuProps={{ PaperProps: { style: { maxHeight: 260 } } }}
              disabled={!swapTargetRoomId}
            >
              <MenuItem value="">{t('accommodationList.selectHorse')}</MenuItem>
              {swapTargetHorses.map((h) => (
                <MenuItem key={h.horse_id} value={String(h.horse_id)}>
                  {h.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label={t('accommodationList.transferDate')}
            type="date"
            value={swapDate}
            onChange={(e) => setSwapDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSwapDialog({
                open: false,
                sourceRoomId: null,
                sourceHorseId: null,
              });
              setSwapTargetRoomId('');
              setSwapTargetHorseId('');
              setSwapTargetHorses([]);
            }}
          >
            {t('accommodationList.cancel')}
          </Button>
          <Button
            onClick={handleSwapSubmit}
            variant="contained"
            disabled={!swapTargetRoomId || !swapTargetHorseId}
          >
            {t('accommodationList.swap')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AvailableRoomsView;
